import React, { useState, useMemo } from 'react';
import { Checkbox, RaisedButton, FlatButton } from 'material-ui';
import CircularProgress from 'material-ui/CircularProgress';
import InfoIcon from 'material-ui/svg-icons/action/info-outline';
import AddAllIcon from 'material-ui/svg-icons/av/playlist-add';
import Popover from 'material-ui/Popover';

import { useBatch, BATCH_TYPES } from './Content/BatchContext';
import { getDiscrepancyLines } from './Content/utils';

const infoPopoverStyles = {
    container: {
        padding: '12px 16px',
        maxWidth: '320px',
        fontSize: '12px',
        lineHeight: '1.5',
    },
    title: {
        fontWeight: 'bold',
        marginBottom: '8px',
        fontSize: '13px',
    },
    section: {
        marginBottom: '10px',
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: '4px',
    },
    colorRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '4px',
    },
    colorBox: {
        width: '16px',
        height: '16px',
        marginRight: '8px',
        borderRadius: '2px',
    },
    borderBox: {
        width: '4px',
        height: '16px',
        marginRight: '8px',
        borderRadius: '1px',
    },
};

/**
 * @param {{ 
 *  type?: string, 
 *  isLoading: boolean, 
 *  onFetch: Function,
 *  onToggleShowUsefulLines: Function,
 *  lineScoreLoading?: boolean,
 *  showUsefulLines?: boolean,
 *  predictorData?: Object,
 *  lineScoreData?: Object,
 *  textLetter?: string,
 * }} props 
 * @returns 
 */
function PredictorType({
    isLoading, 
    onFetch, 
    type, 
    onToggleShowUsefulLines,
    lineScoreLoading,
    showUsefulLines,
    predictorData,
    lineScoreData,
    textLetter,
}) {
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);
    const { addMultipleToBatch } = useBatch();

    const handleInfoHover = (event) => {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoLeave = () => {
        setInfoAnchorEl(null);
    };

    const infoOpen = Boolean(infoAnchorEl);

    // Calculate discrepancies
    const discrepancies = useMemo(() => {
        if (!showUsefulLines || !predictorData || !lineScoreData || !textLetter) {
            return { fiToOi: [], oiToFi: [] };
        }
        return getDiscrepancyLines(predictorData, lineScoreData, textLetter);
    }, [showUsefulLines, predictorData, lineScoreData, textLetter]);

    const discrepancyCount = discrepancies.fiToOi.length + discrepancies.oiToFi.length;

    const handleAddAllDiscrepancies = () => {
        if (discrepancies.fiToOi.length > 0) {
            addMultipleToBatch(discrepancies.fiToOi, BATCH_TYPES.USEFUL_TO_NOT_USEFUL);
        }
        if (discrepancies.oiToFi.length > 0) {
            addMultipleToBatch(discrepancies.oiToFi, BATCH_TYPES.NOT_USEFUL_TO_USEFUL);
        }
    };

    return (
        <div style={{ 
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0px 7px',
            borderRadius: '8px',
            gap: '10px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
        }}
        >
            <RaisedButton 
                className='btn'
                labelStyle={{ fontSize: '10px', color: 'white' }}
                style={{ height: '25px', margin: '10px 0' }}
                buttonStyle={{ height: '25px', lineHeight: '25px', backgroundColor: '#B26E63' }}
                label='Predictor type'
                onClick={onFetch}
                disabled={isLoading}
            />
            <span style={{ fontWeight: 'bold' }}>{type}</span>
            {
                type && type !== 'Error' ?
                    <Checkbox 
                        label='Show useful lines' 
                        onCheck={onToggleShowUsefulLines}
                        style={{ width: 'auto', display: 'inline-block', fontSize: '13px' }}
                        labelStyle={{ width: '120px' }}
                        iconStyle={{ marginRight: '5px' }}
                    />
                    : null
            }
            {lineScoreLoading && (
                <CircularProgress size={18} thickness={2} />
            )}
            {showUsefulLines && !lineScoreLoading && discrepancyCount > 0 && (
                <FlatButton
                    label={`Add ${discrepancyCount} discrepancies`}
                    icon={<AddAllIcon />}
                    onClick={handleAddAllDiscrepancies}
                    style={{ height: '25px', lineHeight: '25px' }}
                    labelStyle={{ fontSize: '10px', textTransform: 'none' }}
                />
            )}
            {showUsefulLines && !lineScoreLoading && (
                <div
                    onMouseEnter={handleInfoHover}
                    onMouseLeave={handleInfoLeave}
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                    }}
                >
                    <InfoIcon 
                        style={{ width: 20, height: 20, color: '#1976d2' }} 
                    />
                    <Popover
                        open={infoOpen}
                        anchorEl={infoAnchorEl}
                        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                        targetOrigin={{ horizontal: 'left', vertical: 'top' }}
                        onRequestClose={handleInfoLeave}
                        useLayerForClickAway={false}
                        style={{ pointerEvents: 'none' }}
                    >
                        <div style={infoPopoverStyles.container}>
                            <div style={infoPopoverStyles.title}>Line Color Legend</div>
                            
                            <div style={infoPopoverStyles.section}>
                                <div style={infoPopoverStyles.sectionTitle}>Border (Original prediction):</div>
                                <div style={infoPopoverStyles.colorRow}>
                                    <div style={{ ...infoPopoverStyles.borderBox, backgroundColor: '#4CAF50' }} />
                                    <span>Green = Freight Info (fi)</span>
                                </div>
                                <div style={infoPopoverStyles.colorRow}>
                                    <div style={{ ...infoPopoverStyles.borderBox, backgroundColor: '#F44336' }} />
                                    <span>Red = Other Info (oi)</span>
                                </div>
                            </div>
                            
                            <div style={infoPopoverStyles.section}>
                                <div style={infoPopoverStyles.sectionTitle}>Background (Current score):</div>
                                <div style={infoPopoverStyles.colorRow}>
                                    <div style={{ ...infoPopoverStyles.colorBox, backgroundColor: 'rgba(76, 175, 80, 0.25)' }} />
                                    <span>Green = Freight Info (fi)</span>
                                </div>
                                <div style={infoPopoverStyles.colorRow}>
                                    <div style={{ ...infoPopoverStyles.colorBox, backgroundColor: 'rgba(244, 67, 54, 0.15)' }} />
                                    <span>Red = Other Info (oi)</span>
                                </div>
                                <div style={infoPopoverStyles.colorRow}>
                                    <div style={{ 
                                        ...infoPopoverStyles.colorBox, 
                                    }} 
                                    />
                                    <span>✓ = Added to batch</span>
                                </div>
                            </div>
                            
                            <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                                Discrepancy = border ≠ background color. Click any line to toggle in/out of batch.
                            </div>
                        </div>
                    </Popover>
                </div>
            )}
        </div>
    );
}

export default PredictorType;


/**
 * @typedef {Object} MailData
 * @property {string} _id
 * @property {string} text
 * @property {string} owner
 */



// WEBPACK FOOTER //
// ./app/components/Letters/Single/Gazetter/PredictorType.jsx